<template>
    <p v-if="isLoading" class="text-muted m-0" aria-live="polite">
        <span
            class="spinner-border spinner-border-sm me-1"
            aria-hidden="true"
        ></span>
        {{ $t('audits.panels.auditImport.loadingAudit') }}
    </p>
    <div v-else-if="errorMessage" class="alert alert-danger mb-0" role="alert">
        {{ errorMessage }}
    </div>
    <AuditTemplateContent
        v-else-if="audit"
        :audit="audit"
        :can-edit="false"
        :show-title="false"
        :user-options="auditorOptions"
        @open-import-audit="$emit('open-import-audit', $event)"
    />
</template>

<script>
import moment from 'moment';
import AuditTemplateContent from './AuditTemplateContent.vue';
import { isObservationAndEvidenceEligibleAudit } from '@/utils/auditVisibility.js';
import { isAuditComplete } from '@/utils/auditStatus';

const AUDIT_FIELDS = [
    'id',
    'hash',
    'name',
    'description',
    'company_id',
    'date',
    'status',
    'completed_at',
    'template_id',
    'form_json',
    'audit_type',
    'app_admin_only',
    'published_to_client',
    'auditor_id',
    'site_id',
    'area_id',
    'details',
    'form_score',
    'form_max_score',
    'score_override',
];

/**
 * One imported audit, read-only: the filled audit. It shows in the child panel of the
 * audit panel, or in a modal when several audits are open side by side.
 * The parent shows the "Open report" link in the footer.
 */
export default {
    name: 'AuditImportPreview',
    components: { AuditTemplateContent },
    props: {
        // One snapshot item: hash, name, auditor_name, area_name, ...
        item: {
            type: Object,
            required: true,
        },
    },
    // loaded: { overviewHref } for the link in the child panel footer
    // open-import-audit: a row of an import question inside this audit
    emits: ['loaded', 'open-import-audit'],
    data() {
        return {
            audit: null,
            isLoading: false,
            errorMessage: '',
        };
    },
    computed: {
        // The read-only form shows the auditor in a disabled select.
        auditorOptions() {
            if (!this.audit?.auditor_id) return [];
            return [
                {
                    text: this.item.auditor_name || '',
                    value: String(this.audit.auditor_id),
                },
            ];
        },
        // Same rule as the audit history list: completed audits with observations or evidence review.
        overviewHref() {
            if (!this.audit?.hash || !isAuditComplete(this.audit)) return null;
            if (!isObservationAndEvidenceEligibleAudit(this.audit)) return null;
            const companyHash =
                this.$route?.name === 'Company Detail'
                    ? this.$route.params?.hash
                    : null;
            const location = companyHash
                ? {
                      name: 'AdminAuditOverview',
                      params: { companyHash, auditHash: this.audit.hash },
                  }
                : { name: 'AuditOverview', params: { hash: this.audit.hash } };
            return this.$router.resolve(location).href;
        },
    },
    watch: {
        'item.hash'() {
            this.loadAudit();
        },
    },
    methods: {
        loadAudit() {
            const hash = this.item?.hash;
            if (!hash) return;
            this.audit = null;
            this.errorMessage = '';
            this.isLoading = true;
            this.$emit('loaded', { overviewHref: null });
            this.$getData('audit/read', {
                fields: AUDIT_FIELDS,
                // The template form decides if the audit has an Overview page.
                include: {
                    template: { fields: ['id', 'form_json'] },
                },
                where: { hash },
            })
                .then((response) => {
                    // A newer request replaced this one.
                    if (hash !== this.item?.hash) return;
                    const record = response?.success
                        ? response.data?.[0]
                        : null;
                    if (!record) {
                        throw new Error(
                            response?.message ||
                                this.$t(
                                    'audits.panels.auditImport.loadAuditError'
                                )
                        );
                    }
                    this.audit = {
                        ...record,
                        date: record.date
                            ? moment.utc(record.date).format('YYYY-MM-DD')
                            : '',
                    };
                    this.$emit('loaded', { overviewHref: this.overviewHref });
                })
                .catch((error) => {
                    if (hash !== this.item?.hash) return;
                    this.errorMessage =
                        error?.message ||
                        this.$t('audits.panels.auditImport.loadAuditError');
                })
                .finally(() => {
                    if (hash === this.item?.hash) this.isLoading = false;
                });
        },
    },
    mounted() {
        this.loadAudit();
    },
};
</script>
